<template>
  <div class="box-content d-flex justify-content-end">
    <div :class="['back', { active: contentActive }]" @click="close()"></div>
    <div :class="['content', { active: contentActive }]">
      <div class="row">
        <h4 class="col-8">{{ title }}</h4>
      </div>

      <div class="col-12">
        <div class="row g-2">
          <form-input
            class="col-12"
            label="Nome do cliente"
            disable="true"
            v-model="clientName"
            :isDisabled="true"
          />

          <form-select
            class="col-12 form-input"
            label="Moeda para recebimento"
            v-model="selectedCurrency"
            :items="currencyOptions"
            id="currencyType"
            name="currencyType"
          />

          <div class="col-12">
            <div v-if="selectedBankData">
              <p>
                <strong>Intermediary Bank:</strong>
                {{ selectedBankData.intermediaryBankName || '-- --' }}
              </p>
              <p>
                <strong>Swift Code:</strong> {{ selectedBankData.intermediaryBankSwift || '-- --' }}
              </p>
              <p class="mt-2">
                <strong>Final Bank:</strong> {{ selectedBankData.beneficiaryBankName || '-- --' }}
              </p>
              <p>
                <strong>Swift Code:</strong> {{ selectedBankData.beneficiaryBankSwift || '-- --' }}
              </p>
              <p>
                <strong>Account Number:</strong>
                {{ selectedBankData.beneficiaryBankAccount || '-- --' }}
              </p>
              <p><strong>Beneficiary:</strong> {{ clientName || '-- --' }}</p>
              <p><strong>IBAN:</strong> {{ selectedBankData.accountIBAN || '-- --' }}</p>
              <p><strong>Details of Charge:</strong> {{ institutionName || '-- --' }}</p>
            </div>

            <div v-else>
              <p>Nenhuma conta disponível para essa moeda.</p>
            </div>
          </div>

          <form-button class="col-6 form-input" title="Copiar dados" @clicked="copyBankData" />
          <form-button class="col-6 form-input" title="Exportar PDF" @clicked="exportBankDataPdf" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchFinancialInstitution } from '@domains/super-admins/financial-institutions/api'
import { handleGlobalError } from '@shared/services/errorHandler'
import { useCache } from '@shared/stores/cache'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formInput, formButton, formSelect } from 'tb-design_system-web'
import { ref, onMounted, watch } from 'vue'
import { toast } from 'vue3-toastify'

export default {
  name: 'formModalDataReceived',

  components: {
    formInput,
    formSelect,
    formButton
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    initialFormData: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['onClose'],

  setup(props, { emit }) {
    const contentActive = ref(false)
    const clientName = ref('')
    const institutionId = ref('')
    const clientId = ref('')
    const selectedCurrency = ref('')
    const institutionName = ref('')
    const currencyOptions = ref([])
    const selectedBankData = ref(null)
    const allBanks = ref([])

    const close = () => {
      contentActive.value = false
      setTimeout(() => emit('onClose'), 300)
    }

    const cacheInstitutions = useCache().getFinancialInstitutions

    const institutionFromCache = cacheInstitutions.find((inst) => inst.id === institutionId.value)

    if (institutionFromCache) {
      institutionName.value = institutionFromCache.name || '-- --'
    }

    const loadBankAccounts = async () => {
      try {
        const response = await fetchFinancialInstitution(institutionId.value, clientId.value)
        const banks = response.data.payload.banks || []
        allBanks.value = banks

        currencyOptions.value = banks.map((bank) => ({
          key: bank.currencyType,
          name: bank.currencyType
        }))

        if (currencyOptions.value.length > 0) {
          selectedCurrency.value = currencyOptions.value[0].key
          updateBankData(selectedCurrency.value)
        }
      } catch (e) {
        handleGlobalError(e)
      }
    }

    const updateBankData = (currency) => {
      selectedBankData.value = allBanks.value.find((acc) => acc.currencyType === currency)
    }

    onMounted(() => {
      if (props.initialFormData) {
        clientName.value = props.initialFormData.clientName
        institutionId.value = props.initialFormData.financialInstitution
        clientId.value = props.initialFormData.clientId

        const cacheInstitutions = useCache().getFinancialInstitutions
        const institutionFromCache = cacheInstitutions.find(
          (inst) => inst.id === institutionId.value
        )

        if (institutionFromCache) {
          institutionName.value = institutionFromCache.name || '-- --'
        }

        loadBankAccounts()
      }

      setTimeout(() => (contentActive.value = true), 100)
    })

    watch(selectedCurrency, (val) => {
      updateBankData(val)
    })

    const copyBankData = () => {
      if (!selectedBankData.value) return

      const content = `
Intermediary Bank: ${selectedBankData.value.intermediaryBankName || '-- --'}
Swift Code: ${selectedBankData.value.intermediaryBankSwift || '-- --'}

Final Bank: ${selectedBankData.value.beneficiaryBankName || '-- --'}
Swift Code: ${selectedBankData.value.beneficiaryBankSwift || '-- --'}

Account Number: ${selectedBankData.value.beneficiaryBankAccount || '-- --'}
Beneficiary: ${clientName.value || '-- --'}
IBAN: ${selectedBankData.value.accountIBAN || '-- --'}

Details of Charge: ${institutionName.value || '-- --'}
`.trim()

      navigator.clipboard
        .writeText(content)
        .then(() => {
          toast('Dados copiados!', {
            theme: 'auto',
            type: 'success',
            dangerouslyHTMLString: true
          })
        })
        .catch((e) => {
          handleGlobalError(e)
        })
    }

    const exportBankDataPdf = () => {
      if (!selectedBankData.value) return

      const doc = new jsPDF()

      doc.setFontSize(16)
      doc.text('Dados Bancários para WIRE TRANSFER em ' + selectedCurrency.value, 105, 20, {
        align: 'center'
      })

      doc.setFontSize(10)
      doc.text('STANDARD SETTLEMENT INSTRUCTIONS (' + selectedCurrency.value + ')', 105, 28, {
        align: 'center'
      })

      const tableData = [
        ['Intermediary Bank:', selectedBankData.value.intermediaryBankName || '-- --'],
        ['Swift Code:', selectedBankData.value.intermediaryBankSwift || '-- --'],
        ['Final Bank:', selectedBankData.value.beneficiaryBankName || '-- --'],
        ['Swift Code:', selectedBankData.value.beneficiaryBankSwift || '-- --'],
        ['IBAN:', selectedBankData.value.accountIBAN || '-- --'],
        ['Account Number:', selectedBankData.value.beneficiaryBankAccount || '-- --'],
        ['Beneficiary:', clientName.value || '-- --'],
        ['Details of Charge:', institutionName.value || '-- --']
      ]

      autoTable(doc, {
        startY: 36,
        body: tableData,
        styles: {
          fontSize: 11,
          cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { fontStyle: 'bold', halign: 'left', cellWidth: 50 },
          1: { halign: 'left' }
        },
        theme: 'grid'
      })

      const filename = `Dados-Bancarios-${clientId.value}-${selectedCurrency.value}.pdf`
      doc.save(filename)
    }

    return {
      contentActive,
      clientName,
      documentId,
      selectedCurrency,
      currencyOptions,
      selectedBankData,
      institutionName,
      close,

      copyBankData,
      exportBankDataPdf
    }
  }
}
</script>

<style scoped>
.box-content {
  position: fixed;
  height: 100%;
  width: 100%;
  z-index: 999;
}
.back {
  background-color: rgba(0, 0, 0, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
}
.content {
  background-color: var(--body-background);
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 44%;
  padding: 44px 38px;
  overflow-y: auto;
  transition: right 0.3s;
}
</style>
